/* eslint-disable no-nested-ternary */
import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MaskedInput from 'react-maskedinput';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import Paragraph from '../Paragraph/Paragraph';
import { ReactComponent as EyeOn } from '../../assets/images/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../assets/images/svg/eye-off.svg';
import IconButton from '../IconButton/IconButton';
import { pxToRem } from '../../assets/styles/helper';
import { uFlexCenter } from '../../assets/styles/utility';

export const ParagraphNormal = styled.p`
  font-size: ${variables.typography.p.normal.fontSize};
  line-height: ${variables.typography.p.normal.lineHeight};
  color: ${({ color }) => color || themeColors.colorTextPrimary};
  font-weight: ${({ weight }) => weight || 400};
  ${({ isDone }) => isDone && `color: ${themeColors.colorTextGrey};`}
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  width: ${({ width }) => width ?? '100%'};
  position: relative;
`;

const TextInputField = styled.input`
  ${({ height }) => height && `height: ${height}px;`}
  width: 100%;
  padding: ${({ hasPrefix }) => (hasPrefix ? '12px 24px' : '12px')};
  border: 1px solid
    ${({ hasError, isTwoStepRegistration }) =>
      hasError
        ? themeColors.colorError
        : isTwoStepRegistration
        ? themeColors.colorRegistrationInputBorder
        : themeColors.colorBorderPrimary};
  border-width: 1px;
  border-style: solid;
  border-radius: ${variables.borderRadius.borderRadius};
  color: ${themeColors.colorTextPrimary};
  ::placeholder {
    color: ${themeColors.colorInfoText};
    font-size: 14px;
  }
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: ${({ hasSuggestion }) =>
    hasSuggestion ? 'capitalize' : 'none'};
  outline: none;
  text-align: ${({ textAlign }) => textAlign ?? 'left'};
  background: ${({ isTwoStepRegistration }) =>
    isTwoStepRegistration
      ? themeColors.colorRegistrationInputBackground
      : 'transparent'};
  z-index: ${({ hasSuggestion }) => (hasSuggestion ? 1 : 'initial')};
  /* This section is required to prevent some browsers from changing the background of the input component on autofill */
  transition: background-color calc(infinity * 1s) step-end,
    background-image calc(infinity * 1s) step-end allow-discrete,
    color calc(infinity * 1s) step-end;
  /* END of Section to prevent some browsers from changing the background of the input component on autofill */
  &:focus {
    border-color: ${themeColors.colorSvgSemidark};
  }
  &:focus::placeholder {
    color: transparent;
  }

  &:disabled {
    background-color: ${themeColors.colorBackgroundSecondary};
  }
`;
const MaskedTextInputField = styled(MaskedInput)`
  ${({ height }) => height && `height: ${height}px;`}
  padding: 12px;
  width: 100%;
  border: 1px solid
    ${({ hasError }) =>
      hasError ? themeColors.colorError : themeColors.colorBorderPrimary};
  border-style: solid;
  border-radius: ${variables.borderRadius.borderRadius};
  color: ${themeColors.colorTextSecondary};
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: ${({ hasSuggestion }) =>
    hasSuggestion ? 'capitalize' : 'none'};
  outline: none;
  pointer-events: ${({ turnOff }) => (turnOff ? 'none' : 'initial')};

  &:focus {
    border-color: ${themeColors.colorPrimary};
  }

  &:disabled {
    background-color: ${themeColors.colorBackgroundSecondary};
  }
`;
const Label = styled(ParagraphNormal)`
  line-height: ${({ isTwoStepRegistration }) =>
    isTwoStepRegistration ? 'normal' : '2.1'};
  font-weight: ${({ isTwoStepRegistration }) =>
    isTwoStepRegistration ? '500' : '600'};
  margin-bottom: ${({ isTwoStepRegistration }) =>
    isTwoStepRegistration ? '5px' : ''};
  font-size: 16px;
`;
export const ErrorMessage = styled(ParagraphNormal)`
  font-size: 14px;
  margin: calc(0.25rem * var(--legacy-scale, 1)) 0;
  color: ${themeColors.colorError};
  font-weight: 500;
  line-height: 1.35;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const EyeIconWrap = styled.div`
  ${uFlexCenter}
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(0, -50%);
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};
  margin-right: ${pxToRem(12)};
  ${({ eyeIconMarginTop, showError }) =>
    eyeIconMarginTop && !showError
      ? `margin-top: ${pxToRem(eyeIconMarginTop)}`
      : ``};
  svg {
    width: ${pxToRem(24)};
    height: ${pxToRem(24)};
    color: ${themeColors.colorInfoTitle};
  }
`;

const Suggestion = styled.span`
  position: absolute;
  color: ${themeColors.colorTextLight};
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: capitalize;
  padding: 12px;
  border: 1px solid transparent;
  top: ${({ hasLabel }) => (hasLabel ? '29px' : 'initial')};
`;

const Suffix = styled.span`
  position: absolute;
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: capitalize;
  padding: 12px 0;
  border: 1px solid transparent;
  left: 65%;
`;

const Prefix = styled(Suffix)`
  left: 15px;
  top: 29px;
`;

const ControlledInput = forwardRef(
  (
    {
      label,
      maxLength,
      width,
      type,
      error,
      suggestion,
      isMasked,
      mask,
      prefix,
      suffix,
      autocomplete,
      autoFocus,
      onInput,
      note,
      noteFontSize,
      isTwoStepRegistration,
      placeholder,
      showEyeIcon,
      eyeIconMarginTop,
      hideErrorOnFocus,
      handleFocus,
      height,
      ...props
    },
    ref,
  ) => {
    const Input = isMasked ? MaskedTextInputField : TextInputField;
    const passwordInput = type === 'password';
    const [inputType, setInputType] = useState(type);
    const showPassword = () => {
      if (inputType === 'password') {
        setInputType('text');
      } else {
        setInputType('password');
      }
    };

    return (
      <InputContainer width={width}>
        {!!label && <Label isTwoStepRegistration>{label}</Label>}
        {!!prefix && <Prefix fontSize={props.fontSize}>{prefix}</Prefix>}
        <InputWrapper>
          <Input
            type={inputType}
            {...props}
            ref={ref}
            hasError={!!error}
            hasSuggestion={!!suggestion}
            hasPrefix={!!prefix}
            mask={mask}
            maxLength={maxLength}
            autoComplete={autocomplete}
            autoFocus={autoFocus}
            onInput={onInput}
            validateOnBlur={false}
            isTwoStepRegistration
            placeholder={placeholder}
            height={height}
            onFocus={handleFocus}
          />
          {!!suffix && <Suffix fontSize={props.fontSize}>{suffix}</Suffix>}
          {passwordInput && showEyeIcon && (
            <EyeIconWrap>
              <IconButton
                tabIndex="-1"
                type="button"
                onClick={() => {
                  showPassword();
                }}
              >
                {inputType === 'password' ? <EyeOff /> : <EyeOn />}
              </IconButton>
            </EyeIconWrap>
          )}
        </InputWrapper>
        {!!suggestion && (
          <Suggestion hasLabel={!!label} fontSize={props.fontSize}>
            {suggestion}
          </Suggestion>
        )}
        {!!error && <ErrorMessage>{error.message}</ErrorMessage>}
        {!!note && (
          <Paragraph
            marginTop={4}
            color={themeColors.colorTextSecondary}
            fontWeight={500}
            fontSize={noteFontSize}
            usePx
          >
            {note}
          </Paragraph>
        )}
      </InputContainer>
    );
  },
);

ControlledInput.propTypes = {
  isMasked: PropTypes.bool,
  mask: PropTypes.string,
  suggestion: PropTypes.string,
  label: PropTypes.string,
  width: PropTypes.string,
  type: PropTypes.oneOf(['text', 'password', 'email', 'number', 'tel']),
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  autocomplete: PropTypes.string,
  turnOff: PropTypes.bool,
  textAlign: PropTypes.string,
  fontSize: PropTypes.string,
  maxLength: PropTypes.string,
  autoFocus: PropTypes.bool,
  onInput: PropTypes.func,
  note: PropTypes.string,
  noteFontSize: PropTypes.oneOf(['string', 'number']),
  showEyeIcon: PropTypes.bool,
  isTwoStepRegistration: PropTypes.bool,
  placeholder: PropTypes.string,
  eyeIconMarginTop: PropTypes.number,
  hideErrorOnFocus: PropTypes.bool,
  handleFocus: PropTypes.func,
  height: PropTypes.number,
};

ControlledInput.defaultProps = {
  type: 'text',
  isMasked: false,
  turnOff: false,
};

ControlledInput.displayName = 'ControlledInput';

export default ControlledInput;
